import React, { useState } from 'react';
import {
    Box,
    TextField,
    IconButton,
    Typography,
    Chip,
    FormHelperText,
    Alert,
} from '@mui/material';
import { Add, Delete } from '@mui/icons-material';
import { useTranslate } from 'react-admin';

interface IpWhitelistInputProps {
    value: string[] | string | null;
    onChange: (value: string[]) => void;
    disabled?: boolean;
}

// Validate IP address or CIDR notation
const validateIpAddress = (ip: string): boolean => {
    // Remove any whitespace
    const trimmed = ip.trim();

    // IPv4 with optional CIDR
    const ipv4Regex = /^(\d{1,3}\.){3}\d{1,3}(\/\d{1,2})?$/;

    // IPv6 with optional CIDR (simplified)
    const ipv6Regex = /^([0-9a-fA-F]{0,4}:){2,7}[0-9a-fA-F]{0,4}(\/\d{1,3})?$/;

    if (!ipv4Regex.test(trimmed) && !ipv6Regex.test(trimmed)) {
        return false;
    }

    // For IPv4, check that each octet is <= 255
    if (ipv4Regex.test(trimmed)) {
        const parts = trimmed.split('/')[0].split('.');
        for (const part of parts) {
            const num = parseInt(part, 10);
            if (num > 255) return false;
        }

        // Check CIDR is valid (0-32 for IPv4)
        if (trimmed.includes('/')) {
            const cidr = parseInt(trimmed.split('/')[1], 10);
            if (cidr < 0 || cidr > 32) return false;
        }
    }

    return true;
};

export const IpWhitelistInput: React.FC<IpWhitelistInputProps> = ({
    value,
    onChange,
    disabled = false,
}) => {
    const translate = useTranslate();
    const [newIp, setNewIp] = useState('');
    const [error, setError] = useState('');

    // Parse the value if it's a string (from JSON)
    const ipList = React.useMemo(() => {
        if (Array.isArray(value)) {
            return value;
        }
        if (typeof value === 'string') {
            try {
                const parsed = JSON.parse(value);
                return Array.isArray(parsed) ? parsed : [];
            } catch {
                // Try to split by common separators
                return value.split(/[,;|\s]+/).filter(ip => ip.trim());
            }
        }
        return [];
    }, [value]);

    const handleAddIp = () => {
        const trimmedIp = newIp.trim();

        if (!trimmedIp) {
            setError(translate('resources.ocpp-servers.ipWhitelist.emptyError'));
            return;
        }

        if (!validateIpAddress(trimmedIp)) {
            setError(translate('resources.ocpp-servers.ipWhitelist.invalidError'));
            return;
        }

        if (ipList.includes(trimmedIp)) {
            setError(translate('resources.ocpp-servers.ipWhitelist.duplicateError'));
            return;
        }

        onChange([...ipList, trimmedIp]);
        setNewIp('');
        setError('');
    };

    const handleRemoveIp = (ipToRemove: string) => {
        onChange(ipList.filter(ip => ip !== ipToRemove));
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            handleAddIp();
        }
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
                {translate('resources.ocpp-servers.ipWhitelist.description')}
            </Typography>

            {/* Input field for new IP */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', minWidth: 0 }}>
                <TextField
                    placeholder="192.168.1.0/24 or 10.0.0.1"
                    value={newIp}
                    onChange={(e) => {
                        setNewIp(e.target.value);
                        setError('');
                    }}
                    onKeyPress={handleKeyPress}
                    disabled={disabled}
                    error={!!error}
                    helperText={error}
                    fullWidth
                    size="small"
                    sx={{ minWidth: 0 }}
                />
                <IconButton
                    onClick={handleAddIp}
                    disabled={disabled || !newIp.trim()}
                    color="primary"
                    sx={{ mt: 0.5, flexShrink: 0 }}
                >
                    <Add />
                </IconButton>
            </Box>

            {/* Display current IP list */}
            {ipList.length > 0 ? (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {ipList.map((ip, index) => (
                        <Chip
                            key={`${ip}-${index}`}
                            label={ip}
                            onDelete={disabled ? undefined : () => handleRemoveIp(ip)}
                            deleteIcon={<Delete />}
                            variant="outlined"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: '0.875rem',
                                maxWidth: '100%',
                                '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                            }}
                        />
                    ))}
                </Box>
            ) : (
                <Alert severity="info">
                    {translate('resources.ocpp-servers.ipWhitelist.emptyList')}
                </Alert>
            )}

            <FormHelperText>
                {translate('resources.ocpp-servers.ipWhitelist.helperText')}
            </FormHelperText>
        </Box>
    );
};